import { Box, Button, Card, CardContent, Container, Grid, Stack, Typography } from "@mui/material";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import FormatQuoteRoundedIcon from "@mui/icons-material/FormatQuoteRounded";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

type ReferenceCard = {
  name: string;
  quote: string;
  detail: string;
};

type KundenbewertungenProps = {
  asPage?: boolean;
  id?: string;
  limit?: number;
  showCta?: boolean;
  layout?: "grid" | "stack";
};

export default function Kundenbewertungen({ asPage = false, id, limit, showCta = false, layout = "grid" }: KundenbewertungenProps) {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!sectionRef.current || isVisible) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [isVisible]);

  const allReferenceCards = useMemo(() => {
    const value = t("home.references.items", { returnObjects: true });
    return Array.isArray(value) ? (value as ReferenceCard[]) : [];
  }, [t]);
  const referenceCards = typeof limit === "number" ? allReferenceCards.slice(0, limit) : allReferenceCards;

  const section = (
    <Box id={id} ref={sectionRef}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "flex-start", md: "flex-end" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2.5 }}
      >
        <Stack spacing={1}>
          <Typography className="section-label">{t("home.testimonials.sectionLabel")}</Typography>
          <Typography variant="h2">{t("home.testimonials.title")}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640 }}>
            Kunden aus Baden-Wuerttemberg vertrauen auf unsere transparente Planung, saubere Ausfuehrung
            und persoenliche Betreuung.
          </Typography>
        </Stack>
        {showCta ? (
          <Button component={RouterLink} to="/referenzen" variant="outlined" color="primary">
            Alle Referenzen ansehen
          </Button>
        ) : null}
      </Stack>

      <Grid container spacing={2.2}>
        {referenceCards.map((card, index) => (
          <Grid item xs={12} md={layout === "stack" ? 12 : (asPage ? 6 : 4)} key={`${card.name}-${card.detail}`}>
            <Card
              elevation={0}
              sx={{
                height: "100%",
                border: "1px solid var(--border)",
                borderRadius: 3,
                background:
                  index % 2 === 0
                    ? "linear-gradient(165deg, color-mix(in srgb, var(--surface) 92%, var(--accent-cool) 8%) 0%, var(--surface) 100%)"
                    : "linear-gradient(165deg, color-mix(in srgb, var(--surface) 94%, var(--accent-warm) 6%) 0%, var(--surface) 100%)",
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateY(0)" : "translateY(18px)",
                transition: "transform 380ms ease, opacity 380ms ease, box-shadow 220ms ease, border-color 220ms ease",
                transitionDelay: `${index * 85}ms`,
                "&:hover": {
                  transform: "translateY(-4px)",
                  borderColor: "color-mix(in srgb, var(--accent) 60%, var(--border))",
                  boxShadow: "0 18px 34px -24px color-mix(in srgb, var(--accent) 80%, transparent)",
                },
              }}
            >
              <CardContent sx={{ p: { xs: 2.2, md: 2.6 } }}>
                <Stack spacing={1.6} sx={{ height: "100%" }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Stack direction="row" spacing={1.1} alignItems="center">
                      <Box
                        sx={{
                          width: 42,
                          height: 42,
                          borderRadius: "50%",
                          background: "color-mix(in srgb, var(--accent) 16%, var(--surface))",
                          color: "var(--accent)",
                          display: "grid",
                          placeItems: "center",
                          fontWeight: 700,
                        }}
                      >
                        {card.name.slice(0, 1)}
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={700}>
                          {card.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {card.detail}
                        </Typography>
                      </Box>
                    </Stack>
                    <FormatQuoteRoundedIcon sx={{ color: "color-mix(in srgb, var(--accent) 55%, var(--muted))" }} />
                  </Stack>

                  <Stack direction="row" spacing={0.2}>
                    {[0, 1, 2, 3, 4].map((star) => (
                      <StarRoundedIcon key={star} sx={{ fontSize: 18, color: "var(--accent-warm)" }} />
                    ))}
                  </Stack>

                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    "{card.quote}"
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  if (!asPage) return section;

  return (
    <Container sx={{ py: { xs: 4, md: 6 } }}>
      <Box className="hero-shell" sx={{ p: { xs: 4, md: 6 }, position: "relative", mb: { xs: 4, md: 6 } }}>
        <Box className="hero-glow" />
        <Stack spacing={1.5} sx={{ position: "relative", zIndex: 1, maxWidth: 820 }}>
          <Typography variant="h1">{t("home.testimonials.title")}</Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Ausgewaehlte Kundenstimmen aus Heilbronn, Stuttgart, Karlsruhe, Ludwigsburg und Umgebung.
          </Typography>
        </Stack>
      </Box>

      <Box className="section-soft" sx={{ mb: 3 }}>
        <Typography variant="h3" sx={{ mb: 1 }}>
          Echte Erfahrungen statt Werbeversprechen
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Unsere Referenzen zeigen, wie wir Projekte von der Erstberatung bis zur Inbetriebnahme
          zuverlaessig umsetzen.
        </Typography>
      </Box>

      {section}
    </Container>
  );
}
